import React, { useEffect, useContext, useMemo, useState } from "react";
import "@/assets/css/filter.css";
import includeIcon from "@/assets/img/include.svg";
import excludeIcon from "@/assets/img/exclude.svg";
import { ReactComponent as RemoveIcon } from "@/assets/img/filter-delete.svg";
import { FilterSearch } from "@/components/filter-search/filter-search";
import styles from "./filter-section.module.css";
import clsx from "clsx";
import { FilterContext } from "./filter-section.component";
import { Button } from "react-bootstrap";
import Tooltip from "@/components/tooltip/tooltip";
import { useTagValues } from "@/services/costExplorer/getDimensionTags.repo";

/**
 * @typedef {import('@/services/costExplorer/addCostExplorerWidget.repo').AddCostExplorerWidgetReqTags} FilterTagItem
 */

/**
 * @typedef TagItemProps
 * @property {FilterTagItem} tag
 * @property {string} service
 * @property {number} index
 * @property {Record<string, string>} tagItems
 */

/**
 * @param {TagItemProps} props
 */
const TagItem = ({ tag, service, index, tagItems, enableBudgetDetails }) => {
  const filterContext = useContext(FilterContext);
  const { data: dimensionValues, isLoading } = useServiceDimensionTags(
    service,
    tag.filterOn,
    enableBudgetDetails?.BudgetName,
    enableBudgetDetails?.Id,
    enableBudgetDetails?.filterValues?.tagsValues
  );

  const filteredValues = useMemo(() => {
    return (
      (tag.values || []).map((e) => ({
        id: e,
        name: e,
      })) || []
    );
  }, [tag.values]);

  const tagLabels = tagItems;

  const tagLabelsFilterOn = useMemo(() => {
    return tagLabels.find((e) => e.id === tag.filterOn);
  }, [tagLabels, tag.filterOn]);

  const selectedTagDimensions = useMemo(() => {
    return filterContext.selectedTags.map((e) => e.filterOn);
  }, [filterContext.selectedTags]);

  const [tempSelectedValues, setTempSelectedValues] = useState(filteredValues);
   const [currentSelectedValues, setCurrentSelectedValues] = useState([]);
    const [applyTrue, setApplyTrue] = useState(false);
 
   const handleCancel = () => {
     setTempSelectedValues(currentSelectedValues);
   };
 
   const handleApply = () => {
     filterContext.setSelectedTags((arr) => {
       const newArr = [...arr];
       newArr[index].values = tempSelectedValues.map((e) => e.id);
       return newArr;
     });
     setCurrentSelectedValues(tempSelectedValues);
   };

   useEffect(() => {
    setTempSelectedValues(filteredValues);
    setCurrentSelectedValues(filteredValues);
  }, [filteredValues]);
  
  const handleDropdownClose = () => {
    setApplyTrue(true);
    document.body.click();
  };

  return (
    <div className={styles.filter_select_box}>
      <div className={styles.service_name_box}>
        <div className={styles.services_name_css}>
          <div className="w-100">
            <FilterSearch
              // options={tagLabels.filter}
              options={tagLabels.filter(
                (tagLabel) => !selectedTagDimensions.includes(tagLabel.id)
              )}
              displayValue="name"
              selectedValues={[tag.filterOn]}
              searchFieldPlaceholder={"Filter Tags"}
              onSelect={(selectedItem) => {
                filterContext.setSelectedTags((arr) => {
                  const newArr = [...arr];
                  newArr[index].filterOn = selectedItem.id;
                  newArr[index].values = [];
                  return newArr;
                });
              }}
              toggle={
                <div
                  className={clsx(styles.filter_dimension_toggle, {
                    [styles.filter_dimension_toggle_selected]:
                      tagLabelsFilterOn,
                  })}
                >
                  {tagLabelsFilterOn ? tagLabelsFilterOn.name : "Add Tags"}
                </div>
              }
            />
          </div>
          {tagLabelsFilterOn && (
            <Tooltip
              tooltipContent="Remove"
              overlayProps={{ placement: "bottom-end" }}
            >
              {(_show, setShow) => (
                <Button
                  variant="link"
                  className={clsx("p-0", styles.remove_icon)}
                  onMouseEnter={() => setShow(true)}
                  onMouseLeave={() => setShow(false)}
                  onClick={() => {
                    if (filterContext?.selectedTags.length > 1) {
                      filterContext?.setSelectedTags((arr) => {
                        const newArr = [...arr];
                        newArr.splice(index, 1);
                        return newArr;
                      });
                    } else {
                      filterContext?.setSelectedTags([
                        {
                          filterOn: "",
                          filterType: "include",
                          values: [],
                        },
                      ]);
                    }
                    handleDropdownClose();
                  }}
                >
                  <RemoveIcon />
                </Button>
              )}
            </Tooltip>
          )}
        </div>
      </div>
      <div className={styles.filter_grouping_box}>
        <div className={styles.filter_group_name_css}>
          {/* ------------------------------------------------------------ */}
          <FilterSearch
            multiselect
            showApply={true}
            applyTrue={applyTrue}
            setApplyTrue={setApplyTrue}
            inclusion={tag.filterType === "include"}
            onInclusionChange={(inclusion) => {
              filterContext.setSelectedTags((s) => {
                const list = [...s];
                list[index].filterType = inclusion;
                return list;
              });
            }}
            options={(dimensionValues || []).map((e) => ({
              id: e?.columnName || "null",
              name: e.columnName || "null",
            }))}
            displayValue="name"
            loading={isLoading}
            selectedValues={tempSelectedValues}
            searchFieldPlaceholder={"Filter " + (tagLabelsFilterOn?.name || "")}
            onSelect={(list) => {
              setTempSelectedValues(list);
            }}
            onRemove={(list) => {
              setTempSelectedValues(list);
            }}
            disabled={!tag?.filterOn?.length}
            toggle={
              <div className="hstack gap-1 mt-1">
                {!tag?.filterOn?.length && (
                  <span className={styles.placeholder_text}>Choose Values</span>
                )}
                {tag?.filterOn?.length > 0 &&
                  tempSelectedValues.length === 0 && (
                    <div className={clsx(styles.chip, "w-100")}>Value</div>
                  )}
                {tempSelectedValues.length >= 1 && (
                  <span className={clsx(styles.chip, "w-100 gap-2 d-flex")}>
                    <img
                      src={
                        tag.filterType === "include" ? includeIcon : excludeIcon
                      }
                      alt=""
                    />
                    <span className={styles.chip_content}>
                      <span className="text-truncate w-100 d-block">
                        {tempSelectedValues
                          .slice(0, 1)
                          .map((e) => e.name || null)
                          .join(",")}
                      </span>
                    </span>
                    {tempSelectedValues.length > 1 && (
                      <span>+{tempSelectedValues.length - 1}</span>
                    )}
                  </span>
                )}
              </div>
            }
           onCancel={() => {
              handleCancel();
            }}
            onApply={() => {
              handleApply();
            }}
          />
          {/* ----------------------------------------------------------- */}
        </div>
      </div>
    </div>
  );
};

/**
 *
 * @param {'aws' | 'azure' | 'gcp'} service
 * @param {string} columnName
 * @param {BudgetName} BudgetName
 * @param {Id} Id
 */
const useServiceDimensionTags = (
  service,
  columnName,
  BudgetName,
  Id,
  TagValues
) => {
  const {
    data: values,
    refetch: fetchValues,
    isLoading,
  } = useTagValues({
    columnName,
    service,
    BudgetName,
    Id,
    TagValues,
  });
  useEffect(() => {
    if (service && columnName) {
      fetchValues();
    }
  }, [columnName, fetchValues, service]);
  return { data: values || [], isLoading };
};

export default TagItem;
